import { Button } from '@/components/ui/button';
import { GrainOverlay, FloatingBlob } from '@/components/ui/GrainOverlay';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { m } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  fadeInRight,
  staggerContainer,
  staggerChild,
  getReducedMotionVariants,
} from '@/lib/motion';
import { useSignupEntityType, useSignupUrl } from '@/hooks/useSignupUrl';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { useTranslation } from '@/i18n/i18n';

/**
 * Calculation evidence card — the strongest rendering of „Widać, skąd wzięła
 * się każda liczba". Content mirrors a REAL agent answer (see
 * src/assets/renders/chat-shell-answer-rates-2x.webp): 12% for PKWiU ex 62.01.1,
 * PIT-28, and the exact statute string our calculators cite
 * (calculators/pkwiu_ryczalt_rates.py). Crisp border = evidence artifact
 * (the one sanctioned crisp-border use, DESIGN.md).
 */
const CalcEvidenceCard = ({ className = '' }: { className?: string }) => {
  const { t } = useTranslation();
  const signupUrl = useSignupUrl({ entityType: 'jdg', source: 'hero_calc_card' });
  return (
    <div
      className={`rounded-2xl border border-foreground/20 bg-card shadow-lg p-4 font-mono text-sm sm:p-6 ${className}`}
    >
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
        {t('hero.card.kicker')}
      </p>
      <dl className="mt-4 space-y-2 tabular-nums">
        <div className="flex items-baseline justify-between gap-6">
          <dt className="text-muted-foreground">{t('hero.card.rateLabel')}</dt>
          <dd className="text-2xl font-bold text-foreground">12%</dd>
        </div>
        <div className="flex items-baseline justify-between gap-6">
          <dt className="text-muted-foreground">{t('hero.card.advanceLabel')}</dt>
          <dd className="text-foreground">{t('hero.card.advanceValue')}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-6">
          <dt className="text-muted-foreground">{t('hero.card.returnLabel')}</dt>
          <dd className="text-foreground">PIT-28</dd>
        </div>
      </dl>
      <div className="mt-4 border-t border-border pt-3">
        <p className="text-xs text-muted-foreground">
          <mark className="bg-primary/30 text-foreground px-1 rounded-sm">
            {t('hero.card.statute')}
          </mark>
        </p>
        <a
          href={signupUrl}
          onClick={() => trackLandingCtaClicked('hero_calc_card', t('hero.card.proofCta'))}
          className="mt-3 inline-flex min-h-11 items-center gap-1 rounded-lg border border-border px-3 text-xs text-foreground underline underline-offset-4 hover:no-underline sm:min-h-0 sm:border-0 sm:px-0"
        >
          {t('hero.card.proofCta')}
          <ArrowRight className="size-3" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
};

/**
 * Paper props behind the card — CSS-composed documents (faktura, wyciąg,
 * Dziennik Ustaw). Props, not product UI, so the real-render rule doesn't
 * bind them (KTD2) — but their content stays honest: the invoice arithmetic
 * is correct (12 300 × 23% = 2 829) and the statement's incoming transfer
 * matches the invoice gross, so the evidence trail reads true. Polish text is
 * intentional on every locale: these are authentic Polish documents.
 */
const PaperStack = () => {
  const { t } = useTranslation();
  return (
    <div className="relative h-[540px] select-none">
      {/* AT description of the decorative paper composition; the calc card
          below is real content (its link must stay announced — WCAG 4.1.2,
          which role="img" on the wrapper would have broken). */}
      <p className="sr-only">{t('hero.paperAria')}</p>
      {/* Dziennik Ustaw — statute page with the highlighted article */}
      <div
        aria-hidden="true"
        className="absolute right-2 top-2 w-[320px] rotate-[4deg] rounded-md border border-border bg-background-alt shadow-md p-5 font-serif"
      >
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-center">
          Dziennik Ustaw
        </p>
        <p className="mt-1 text-[10px] text-muted-foreground text-center">
          Ustawa o zryczałtowanym podatku dochodowym
        </p>
        <div className="mt-3 space-y-1.5 text-[10px] leading-relaxed text-foreground/60">
          <p>Art. 12. 1. Ryczałt od przychodów ewidencjonowanych wynosi:</p>
          <p className="pl-2">2a) 14% przychodów ze świadczenia usług…</p>
          <p className="pl-2 bg-primary/30 text-foreground rounded-sm px-1">
            2b) lit. b: 12% przychodów ze świadczenia usług związanych z oprogramowaniem
          </p>
          <p className="pl-2">3) 10% przychodów ze świadczenia usług…</p>
        </div>
      </div>

      {/* Wyciąg bankowy — the transfer matches the invoice gross */}
      <div
        aria-hidden="true"
        className="absolute left-0 top-24 w-[330px] -rotate-[3deg] rounded-md border border-border bg-background-alt shadow-md p-5 font-mono text-[10px]"
      >
        <p className="uppercase tracking-widest text-muted-foreground">Wyciąg bankowy · 06/2026</p>
        <div className="mt-3 space-y-1.5 tabular-nums text-foreground/70">
          <p className="flex justify-between gap-4">
            <span>03.06 · przelew przychodzący</span>
            <span className="text-foreground">+15 129,00 zł</span>
          </p>
          <p className="flex justify-between gap-4">
            <span>10.06 · składka ZUS (DRA)</span>
            <span>−2 757,34 zł</span>
          </p>
          <p className="flex justify-between gap-4">
            <span>20.06 · zaliczka ryczałt</span>
            <span>−1 194,95 zł</span>
          </p>
        </div>
      </div>

      {/* Faktura — honest arithmetic: 12 300 netto, 23% VAT, 15 129 brutto */}
      <div
        aria-hidden="true"
        className="absolute left-10 bottom-10 w-[340px] rotate-[1.5deg] rounded-md border border-border bg-background-alt shadow-md p-5 font-mono text-[10px]"
      >
        <p className="uppercase tracking-widest text-muted-foreground">Faktura VAT · 06/2026</p>
        <p className="mt-1 text-foreground/60">{t('hero.paper.invoiceSubject')}</p>
        <div className="mt-3 space-y-1.5 tabular-nums text-foreground/70">
          <p className="flex justify-between gap-4">
            <span>Netto</span>
            <span>12 300,00 zł</span>
          </p>
          <p className="flex justify-between gap-4">
            <span>VAT 23%</span>
            <span>2 829,00 zł</span>
          </p>
          <p className="flex justify-between gap-4 font-bold text-foreground">
            <span>Brutto</span>
            <span>15 129,00 zł</span>
          </p>
        </div>
      </div>

      {/* The evidence card floats above the papers */}
      <CalcEvidenceCard className="absolute left-16 top-40 w-[360px] z-10" />
    </div>
  );
};

export const Hero = () => {
  const shouldReduceMotion = useReducedMotion();
  const signupUrl = useSignupUrl({ source: 'hero' });
  const signupEntityType = useSignupEntityType();
  const sectionRef = useLandingSectionTracking('hero');
  const { t } = useTranslation();
  // Observe the claim itself: mobile hides it, and mount is not visibility evidence.
  const filingClaimRef = useLandingSectionTracking<HTMLSpanElement>('hero_filing_claim', {
    threshold: 0.1,
  });

  return (
    <section
      ref={sectionRef}
      aria-label={t('hero.ariaLabel')}
      className="relative flex flex-col items-center overflow-hidden bg-background"
    >
      {/* Premium background effects */}
      <div className="absolute inset-0 hero-glow" />
      <FloatingBlob position="top-left" size="xl" delay={0} className="left-1/4 top-20" />
      <FloatingBlob
        position="bottom-right"
        size="lg"
        delay={2000}
        className="right-1/4 bottom-20"
      />

      {/* Grain texture overlay */}
      <GrainOverlay opacity={0.05} />

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-8 py-5 sm:py-8 md:py-10 xl:py-12 z-10">
        <div className="grid xl:grid-cols-[1fr_1.1fr] gap-7 md:gap-10 xl:gap-16 items-start">
          {/* Left column - Content */}
          <m.div
            className="text-left space-y-5 md:space-y-6 relative z-10"
            initial="hidden"
            animate="visible"
            variants={
              shouldReduceMotion ? getReducedMotionVariants(staggerContainer) : staggerContainer
            }
          >
            {/* Trust badge */}
            <m.div
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-card/80 backdrop-blur-sm rounded-full text-xs font-semibold text-foreground shadow-sm border border-primary/20 sm:gap-2.5 sm:px-4 sm:py-2 sm:text-sm"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              <span className="flex size-2.5 rounded-full bg-primary animate-pulse"></span>
              <span>{t('hero.badge')}</span>
              <Sparkles className="size-4 text-primary" />
            </m.div>

            {/* Platform heading — the engine/trust story (BOO-3230 Wave 2) */}
            <m.div
              className="space-y-3 md:space-y-4"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              <h1 className="text-[2rem] sm:text-[2.35rem] md:text-5xl xl:text-[3.25rem] font-semibold tracking-tight text-foreground leading-[1.06] md:leading-[1.1]">
                {t('hero.platformHeading')}
                <span className="mt-2 block text-xl sm:text-2xl md:text-[1.75rem] xl:text-3xl leading-snug text-gradient-yellow">
                  {t('hero.platformHeadingAccent')}
                </span>
              </h1>

              <p className="text-base md:text-xl text-muted-foreground max-w-xl leading-snug sm:leading-relaxed">
                {t('hero.subheading')}{' '}
                <span className="text-foreground font-medium">{t('hero.subheadingBold')}</span>{' '}
                <span ref={filingClaimRef} className="hidden sm:inline">
                  {t('hero.subheadingSuffix')}
                </span>
              </p>
            </m.div>

            {/* CTA buttons */}
            <m.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              <Button
                size="lg"
                className="w-full text-base font-bold px-8 h-14 rounded-xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300 sm:w-auto"
                asChild
              >
                <a
                  id="hero-primary-cta"
                  href={signupUrl}
                  onClick={() =>
                    trackLandingCtaClicked('hero_primary', t('hero.ctaPrimary'), {
                      entity_type: signupEntityType,
                    })
                  }
                >
                  {t('hero.ctaPrimary')}
                </a>
              </Button>
            </m.div>

            <m.p
              data-testid="hero-price-line"
              className="text-sm text-muted-foreground max-w-xl leading-snug"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              {t('hero.priceLine')}
            </m.p>

            <m.p
              data-testid="hero-memorable"
              className="font-mono text-xs leading-relaxed text-muted-foreground max-w-xl"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              {t('hero.memorable')}
            </m.p>

            {/* Friction reducers */}
            <m.div
              className="flex flex-wrap gap-x-3 gap-y-2"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              {[t('hero.noCommitment'), t('hero.setupTime')].map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm"
                >
                  <Check className="size-3.5 text-success" />
                  {item}
                </span>
              ))}
            </m.div>

            {/* Mobile decision (plan open item 7): below xl the calc card renders
                standalone, full-width and readable; the paper stack is xl-only.
                No cropping, no overlap juggling on small screens. */}
            <m.div
              className="xl:hidden"
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
            >
              <CalcEvidenceCard className="max-w-md" />
            </m.div>
          </m.div>

          {/* Right column — Papier composition (variant-B, no icon circles) */}
          <m.div
            className="hidden xl:block relative"
            initial="hidden"
            animate="visible"
            variants={shouldReduceMotion ? getReducedMotionVariants(fadeInRight) : fadeInRight}
          >
            <PaperStack />
          </m.div>
        </div>
      </div>
    </section>
  );
};

/**
 * The hero's audience statement and trust strip, rendered under the door band (JDG conversion
 * slice 2): inside the hero they pushed the "Prowadzę JDG" door, the strongest sign-up CTA,
 * below the first screen. Mobile keeps the conversion path short, as before.
 */
export const HeroAudience = () => {
  const { t } = useTranslation();
  return (
    <section
      data-testid="hero-audience"
      className="hidden sm:block px-5 sm:px-6 md:px-8 py-8 bg-background border-b border-border"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        <p className="text-base text-muted-foreground max-w-3xl leading-relaxed">
          {t('hero.audience')}
        </p>
        <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
          {[t('hero.trust1'), t('hero.trust2'), t('hero.trust3'), t('hero.trust4')].map((text) => (
            <div key={text} className="flex items-center gap-2">
              <Check className="size-4 text-success" />
              <span className="text-foreground/70">{text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
